import moment from 'moment';

/* eslint-disable no-console */
/* eslint-disable default-case */
/* eslint-disable no-param-reassign */
import BaseDataTransformer from '../base-data-transformer/base-data-transformer';

class PlainChartDataToGroupTransformer extends BaseDataTransformer {
  // The columns should be ordered by their level of grouping, the aggregation column should not be between grouping columns

  /*
   * Transforms/Groups a datastructure like this
   *
   * group  |   name   |   a
   * g1     |   n1     |   x
   * g1     |   n2     |   x
   * g2     |   n3     |   x
   * g2     |   n4     |   x
   * g2     |   n5     |   x
   *
   * grouped by 'group'
   * with an aggregation agg(a)
   *
   * into this
   *
   * [
   *  {
   *      name: g1,
   *      a: agg(x),
   *      agg(a): agg(x),
   *      values: [
   *          { name: n1, a: x, agg(a): x },
   *          { name: n2, a: x, agg(a): x },
   *      ]
   *      },
   *  },
   *  {
   *      name: g2,
   *      a: agg(x),
   *      agg(a): agg(x),
   *      values: [
   *          { name: n3, a: x, agg(a): x },
   *          { name: n4, a: x, agg(a): x },
   *          { name: n5, a: x, agg(a): x },
   *      ]
   *      },
   *  },
   * ]
   *
   *  Note the duplication of a and agg(a) on every level to allow recursive sorting and data display on every level.
   *  This is necessary because of the non-consistent naming: sql-result != aggregationColumn != orderingColumn
   */


  transform(data, options) {
    // check options
    if (!Array.isArray(options.groupByColumn)) {
      options.groupByColumn = [options.groupByColumn];
    }

    if (!options || !options.groupByColumn || !options.groupByColumn[0]) {
      console.log("Please provide the 'groupByColumn' option!");
      return [];
    }

    // skip invalid data
    if (!data.columns) {
      return [];
    }

    // get index of first grouping column
    const indexOfGroupColumn = data.columns.indexOf(options.groupByColumn[0]);
    if (indexOfGroupColumn < 0) {
      console.log(`Could not find index of column '${options.groupByColumn[0]}'`);
      return [];
    }

    const resultDictionary = [];
    data.data.forEach((row) => {
      // use value of index column as group-name, push all other values in row to twodimensional array
      const name = row.values[indexOfGroupColumn];
      const rowValues = row.values.slice();
      rowValues.splice(indexOfGroupColumn, 1);

      // lookup result[name]
      if (!resultDictionary[name]) {
        resultDictionary[name] = {
          name,
          values: [],
        };
      }

      // push remaining values to group
      const nonGroupedColumns = data.columns.slice();
      nonGroupedColumns.splice(indexOfGroupColumn, 1);

      resultDictionary[name].values.push(rowValues);
    });

    // dictionary to array
    const result = Object.values(resultDictionary);

    // calculate some aggregations, if needed
    this.calculateAggregations(data, result, options);

    // return if there are no more columns to group by
    if (options.groupByColumn.length < 2) {
      const columns = data.columns.filter(c => c !== options.groupByColumn[0]);

      result.forEach((r) => {
        r.values = r.values.map(value => this.mapValuesToObjects(value, columns, options.aggregations));
      });

      return result;
    }

    // create new options without current grouping column
    const newOptions = {
      ...options,
      groupByColumn: options.groupByColumn.slice(1),
    };

    // recursive calculation of values
    result.forEach((r) => {
      r.values = this.transform({
        ...data,
        columns: data.columns.filter(c => c !== options.groupByColumn[0]),
        data: r.values.map(column => ({ values: column })),
      }, newOptions);
    });


    return result;
  }


  // If no aggregationColumns are available, all non-numeric values are added to the name
  // If there are aggregationColumns available, all non-aggregation values are added to the name
  // Numeric values will be converted to numbers
  mapValuesToObjects = (value, columns, aggregationColumns) => {
    const valueObject = { name: '' };
    columns.forEach((c, i) => {
      const date = moment(value[i], 'DD.MM.YYYY HH:mm', true);

      const val = this.forceFloat(value[i]);
      const isNan = Number.isNaN(val) || date.isValid();

      valueObject[c] = isNan ? value[i] : val;
      const aggregation = aggregationColumns ? aggregationColumns.find(ac => ac.column === c) : null;
      if (aggregation) {
        valueObject[aggregation.propertyNameInResult] = isNan ? value[i] : val;
      } else if (aggregationColumns || isNan) {
        valueObject.name += `${value[i]} `;
      }
    });

    return valueObject;
  };

  calculateAggregations(originalData, groups, options) {
    if (!options.aggregations) return;

    options.aggregations.forEach((aggregation) => {
      if (!aggregation.method || !aggregation.column) {
        // eslint-disable-next-line max-len
        console.log("Invalid or missing parameters provided for aggregation. Expected at least 'method' and 'column'!");
        return;
      }

      let aggregationColumnIndex = originalData.columns.indexOf(aggregation.column);

      if (aggregationColumnIndex < 0) {
        console.log(`Could not find aggregation column '${aggregation.column}'`);
        return;
      }

      // substract one from aggregationColumnIndex if it is not yet 0
      if (aggregationColumnIndex > 0) {
        aggregationColumnIndex--;
      }

      let aggregationFunction = () => {};
      switch (aggregation.method) {
        case 'sum': aggregationFunction = (l, r) => this.forceFloat(l) + this.forceFloat(r || 0); break;
        case 'product': aggregationFunction = (l, r) => this.forceFloat(l) * this.forceFloat(r || 1); break;
      }

      // group[column] for recursive behaviour, propertyName for display
      groups.forEach((group) => {
        const value = this.aggregate(group.values, aggregationColumnIndex, aggregationFunction);
        group[aggregation.column] = value;
        group[aggregation.propertyNameInResult] = value;
      });
    });
  }

  aggregate(data, valueKey, aggregationFunction) {
    if (data.length === 0) return null;
    if (data.length === 1) return aggregationFunction(data[0][valueKey], null);

    let result = data[0][valueKey];
    for (let i = 1; i < data.length; i++) {
      result = aggregationFunction(result, data[i][valueKey]);
    }
    return result;
  }
}

export default PlainChartDataToGroupTransformer;
